import React, { useState } from 'react';

import { MapTo } from '@adobe/aem-react-editable-components';
import logoutIcon from 'assets/pics/icon-close.svg';
import { useAuth } from 'context/Auth';
import { useCurrentInstaller } from 'hooks/UseCurrentInstaller';
import { useSvgFetch } from 'hooks/useSvgFetch';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import { DividingLine } from 'components/DividingLine/DividingLine';
import { ErrorPopup } from 'components/ErrorPopup/ErrorPopup';
import { HamburgerMenuComponent } from 'components/HamburgerMenuComponent';
import { HomePageHeaderReportDisorderButton } from 'components/HomePageHeaderReportDisorderButton';
import { HomePageHeaderSubLink } from 'components/HomePageHeaderSubLink';
import { TenantSwitchButton } from 'components/TenantSwitch/TenantSwitchButton/TenantSwitchButton';
import { TenantSwitchModal } from 'components/TenantSwitch/TenantSwitchModal/TenantSwitchModal';

import { logoutWithRedirect } from 'utils/AuthUtils';
import { getFileExtension } from 'utils/getFileExtension';

import './css/HomePageHeader.sass';

const HeaderEditConfig = {
  emptyLabel: 'Header',
  isEmpty(props) {
    return Boolean(!props.navigation?.items?.length);
  },
};

// fallback navroot for the case that navigation is not configured yet in AEM i.e. navigation.items=[]
const emptyNavRoot = {
  url: '#',
  title: 'Startseite',
  children: [],
};
const HomePageHeader = (props) => {
  const { navigation, metaNav, disorderReportLabel, logo, mobileLogo } = props;
  const navRoot = navigation?.items?.[0] || emptyNavRoot;
  const navLinks = (navRoot.children || []).filter(
    (navLink) => typeof navLink?.url === 'string' && navLink.url
  );
  const switchLinks = metaNav?.links;

  const [isTenantSwitchOpen, setIsTenantSwitchOpen] = useState(false);

  const { installer } = useCurrentInstaller({ suppressError: true });
  const { isCIAM } = useAuth();

  const logoData = {
    image: useSvgFetch(logo) || logo,
    isSvg: getFileExtension(logo) === 'svg',
    mobileLogo: useSvgFetch(mobileLogo) || mobileLogo,
  };

  const stoerungenNavLinkData = navLinks.find((navLink) => navLink.url.includes('stoerungen'));

  const renderHeaderMobile = () => (
    <div className="header phone">
      <button
        className="home-page-header-logout-button-mobile"
        onClick={() => logoutWithRedirect(isCIAM)}>
        <Link to={navRoot.url}>
          <img alt="logout-icon" src={logoutIcon} />
          Ausloggen
        </Link>
      </button>
      <span className="home-page-header-top-section-mobile">
        <Link to={navRoot.url}>
          <div className="home-page-header-logo-mobile-wrapper">
            <img
              alt="Efix_Logo"
              className="home-page-header-logo-mobile"
              src={logoData.mobileLogo || logoData.image}
            />
          </div>
        </Link>
        <TenantSwitchButton
          className="home-page-header-tenant-switch-button"
          onClick={() => {
            setIsTenantSwitchOpen(true);
          }}
        />
      </span>
      <DividingLine />
      <div className="header-mobile-links">
        <HamburgerMenuComponent navRoot={navRoot} navLinks={navLinks} installer={installer} />
        {stoerungenNavLinkData && (
          <HomePageHeaderReportDisorderButton
            disorderReportLabel={disorderReportLabel}
            navLinkData={stoerungenNavLinkData}
          />
        )}
      </div>
    </div>
  );

  const renderHeaderDesktop = () => (
    <>
      <div className="header desktop">
        <Link to={navRoot.url}>
          {logoData.isSvg ? (
            <span className="header-logo" dangerouslySetInnerHTML={{ __html: logoData.image }} />
          ) : (
            <img alt="Efix_Logo" className="header-logo" src={logoData.image} />
          )}
        </Link>
        <div className="home-page-header-logout-button">
          <TenantSwitchButton
            className="home-page-header-tenant-switch-button"
            onClick={() => {
              setIsTenantSwitchOpen(true);
            }}
          />
          <button
            className="home-page-header-logout-button"
            onClick={() => logoutWithRedirect(isCIAM)}>
            <Link to={navRoot.url}>
              <img alt="logout-icon" src={logoutIcon} /> Ausloggen
            </Link>
          </button>
        </div>
      </div>
      <DividingLine />
      <div className="home-page-header-links">
        <HomePageHeaderSubLink name={navRoot.title} link={navRoot.url} key={navRoot.title} />
        {navLinks.map((link) => (
          <HomePageHeaderSubLink
            name={link.title}
            link={link.url}
            key={link.title}
            installer={installer}
          />
        ))}
        {stoerungenNavLinkData && (
          <HomePageHeaderReportDisorderButton
            disorderReportLabel={disorderReportLabel}
            navLinkData={stoerungenNavLinkData}
          />
        )}
      </div>
    </>
  );

  return (
    <>
      <header className="home-page-header">
        {renderHeaderMobile()}
        {renderHeaderDesktop()}
      </header>
      <TenantSwitchModal
        isOpen={isTenantSwitchOpen}
        onClose={() => setIsTenantSwitchOpen(false)}
        links={switchLinks}
      />
      <ErrorPopup />
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default MapTo('e-fix/components/structure/header')(HomePageHeader, HeaderEditConfig);

HomePageHeader.propTypes = {
  navigation: PropTypes.object,
  metaNav: PropTypes.object,
  disorderReportLabel: PropTypes.string,
  logo: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  mobileLogo: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
};

HomePageHeader.defaultProps = {
  navigation: {},
  metaNav: {},
  disorderReportLabel: '',
  logo: '',
  mobileLogo: '',
};
